import { createMemoryHistory } from "history"
import { useEffect, useMemo } from "react"
import { StringParam, useQueryParams } from "use-query-params"

export type UseQueryParamStateReturn = ReturnType<typeof useQueryParamState>

export function useQueryParamState(queryParamName: string) {
  // Using useQueryParams in order to manage the query parameter better
  // (than if using useQueryParam) since we can pass undefined to remove the query param.
  const [queryParams, setQueryParams] = useQueryParams({
    [queryParamName]: StringParam
  })
  const queryParamPath = queryParams[queryParamName] || null

  // Passing undefined removes the query param.
  function setQueryParamPath(path?: string) {
    setQueryParams({
      [queryParamName]: path
    })
  }

  return {
    queryParamPath,
    setQueryParamPath
  }
}

export type UseQueryParamHistoryReturn = ReturnType<typeof useQueryParamHistory>

function useQueryParamHistory(queryParamName: string) {
  // Using useQueryParams in order to manage the query parameter better
  // (than if using useQueryParam) since we can pass undefined to remove the query param.
  const { queryParamPath } = useQueryParamState(queryParamName)

  // Create history with initial entries
  const history = useMemo(
    () =>
      createMemoryHistory({
        initialEntries: queryParamPath ? [queryParamPath] : ["/"],
        initialIndex: 0
      }),
    []
  )

  // Update memory router when query param changes externally
  useEffect(() => {
    if (queryParamPath) {
      let newQueryParamPath = queryParamPath
      // Ensure path has leading slash so we can use it as a pathname.
      if (!queryParamPath.startsWith("/")) {
        newQueryParamPath = `/${newQueryParamPath}`
      }

      // Check if the current path is different from what we want.
      if (history.location?.pathname !== newQueryParamPath) {
        history.replace(newQueryParamPath)
      }
    }
  }, [queryParamPath])

  return history
}

export default useQueryParamHistory
